import { valueExists } from '@mw/helpers';

import {
  getConversations,
  getConversationsCursor,
} from '~/selectors/conversations';

import {
  addConversations,
  setSelectedConversation as setSelectedConversationAction,
  setConversations,
} from '~/actions/conversations';

import { getPrimus } from '~/utils/primus';
import { getStore } from '~/store/helpers';
import { filterSubscriptions } from '~/utils/agents';
import { CLIENT_ORIGIN_WS_EVENTS } from '~/constants';
import { MESSAGES_CHUNK_SIZE } from '~/api/constants';
import { createConversationReducer } from '~/reducers/conversations';
import { setNotificationStatus } from '~/actions/notificationStatus';

import {
  getConversationByID,
  getConversations as getConversationsRequest,
} from '~/api/conversations';

import {
  getAgents,
  getConversationsOnlyUnread,
  getConversationsOrder,
  getChannels,
} from '~/selectors';

const getChannelTypes = (agentsSubscribedTo) => [
  ...new Set(agentsSubscribedTo.map(({ channel_type }) => channel_type)),
];

const handleConversationMessages =
  (conversation) => async (dispatch, getState) => {
    const state = getState();
    const primus = getPrimus();
    const store = getStore();

    const agents = getAgents(state);
    const channels = getChannels(state);
    const { notificationStatus } = state;

    const customerID = conversation.id;

    store.reducerManager.add(
      `conversation_${customerID}`,
      createConversationReducer(customerID, agents, channels)
    );

    primus.emit(CLIENT_ORIGIN_WS_EVENTS.COLLECTION, {
      cid: 'messages',
      limit: MESSAGES_CHUNK_SIZE,
      sort: 'sent',
      order: -1,
      filters: { customer_id: customerID },
    });

    const isNotified = Object.values(
      conversation.chatbot_notification_received
    ).some(Boolean);

    // eslint-disable-next-line no-bitwise
    if (isNotified ^ notificationStatus[customerID]) {
      dispatch(
        setNotificationStatus({
          customer_id: customerID,
          isNotified,
        })
      );
    }
  };

export const fetchConversations =
  ({ search } = {}) =>
  async (dispatch, getState) => {
    const state = getState();

    const agents = getAgents(state);
    const order = getConversationsOrder(state);
    const onlyUnread = getConversationsOnlyUnread(state);

    const { conversations, cursor } = await getConversationsRequest({
      order,
      search,
      onlyUnread,
    });

    for (const conversation of conversations) {
      const agentsSubscribedTo = filterSubscriptions(conversation, agents);

      conversation.channelTypes = getChannelTypes(agentsSubscribedTo);
      conversation.agentsIDsSubscribedTo = agentsSubscribedTo.map(
        ({ id }) => id
      );

      dispatch(handleConversationMessages(conversation));
    }

    dispatch(setConversations({ conversations, fetchCursor: cursor }));
  };

export const fetchMoreConversations =
  ({ search } = {}) =>
  async (dispatch, getState) => {
    const state = getState();
    const agents = getAgents(state);
    const order = getConversationsOrder(state);
    const currentCursor = getConversationsCursor(state);
    const onlyUnread = getConversationsOnlyUnread(state);

    if (currentCursor === null) {
      return;
    }

    const { conversations, cursor: newCursor } = await getConversationsRequest({
      cursor: currentCursor,
      order,
      search,
      onlyUnread,
    });

    for (const conversation of conversations) {
      const agentsSubscribedTo = filterSubscriptions(conversation, agents);

      conversation.channelTypes = getChannelTypes(agentsSubscribedTo);
      conversation.agentsIDsSubscribedTo = agentsSubscribedTo.map(
        ({ id }) => id
      );

      dispatch(handleConversationMessages(conversation));
    }

    dispatch(addConversations({ conversations, fetchCursor: newCursor }));
  };

export const fetchConversation =
  (conversationID) => async (dispatch, getState) => {
    const state = getState();
    const agents = getAgents(state);

    let conversation;

    try {
      conversation = await getConversationByID(conversationID);
    } catch (error) {
      return;
    }

    const agentsSubscribedTo = filterSubscriptions(conversation, agents);

    conversation.channelTypes = getChannelTypes(agentsSubscribedTo);
    conversation.agentsIDsSubscribedTo = agentsSubscribedTo.map(({ id }) => id);

    dispatch(handleConversationMessages(conversation));

    dispatch(addConversations({ conversations: [conversation] }));
  };

export const addConversation = (conversation) => (dispatch, getState) => {
  const state = getState();

  const agents = getAgents(state);
  const agentsSubscribedTo = filterSubscriptions(conversation, agents);

  dispatch(
    addConversations({
      conversations: [
        {
          ...conversation,
          channelTypes: getChannelTypes(agentsSubscribedTo),
          agentsIDsSubscribedTo: agentsSubscribedTo.map(({ id }) => id),
        },
      ],
    })
  );
};

export const updateConversationsWithRemovedChannel =
  (channelType) => (dispatch, getState) => {
    const state = getState();

    const agents = getAgents(state);
    const conversations = getConversations(state);
    const cursor = getConversationsCursor(state);

    const updatedConversations = conversations.map((conversation) => {
      if (!conversation.channelTypes.includes(channelType)) {
        return conversation;
      }

      const agentsSubscribedTo = filterSubscriptions(conversation, agents);

      return {
        ...conversation,
        channelTypes: getChannelTypes(agentsSubscribedTo),
        agentsIDsSubscribedTo: agentsSubscribedTo.map(({ id }) => id),
      };
    });

    dispatch(
      setConversations({
        conversations: updatedConversations,
        fetchCursor: cursor,
      })
    );
  };

export const setSelectedConversation =
  (conversationID) => (dispatch, getState) => {
    const state = getState();
    const conversations = getConversations(state);

    const selectedConversation = conversations.find(
      (conversation) => conversation.id === conversationID
    );

    if (valueExists(selectedConversation)) {
      dispatch(
        setSelectedConversationAction({
          selectedConversation,
        })
      );
    } else {
      dispatch(fetchConversation(conversationID));
    }
  };
